import { useCallback, useEffect, useState } from "react";
import { Button, Empty, Select, Space, Spin, Table, Tag, Tooltip } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import moment from "moment";
import ApiService from "../../../core/service/api-service";
import { Pages } from "../../../AppRouter";

// Aralık seçenekleri (dakika). Sayaçlar 5 dk'lık kovalarda tutulduğu için en küçük anlamlı aralık 15 dk.
const RANGE_OPTIONS = [
    { value: 15, label: '15 dk' },
    { value: 60, label: '1 saat' },
    { value: 60 * 24, label: '24 saat' },
    { value: 60 * 24 * 7, label: '7 gün' },
];

export default function RouteStatsPanel() {
    const { t } = useTranslation();
    const history = useHistory();

    const [minutes, setMinutes] = useState(60 * 24);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const from = moment().subtract(minutes, 'minutes').toISOString();
        const response = await ApiService.Log.GetRouteStats(from, moment().toISOString());
        setLoading(false);

        if (response.success)
            setRows(response.data ?? []);
    }, [minutes]);

    useEffect(() => {
        load();
    }, [load]);

    const totalRequests = rows.reduce((sum, row) => sum + row.count, 0);

    // Endpoint'e tıklayınca o route'un ham logları: Uygulama Logları sekmesine route filtresiyle git.
    const openLogs = (row) => {
        history.push(`${Pages.Log.AppLogs}?route=${encodeURIComponent(row.route)}`);
    };

    const columns = [
        {
            title: t('HTTP Path'),
            dataIndex: 'route',
            key: 'route',
            render: (route, row) => (
                <a onClick={() => openLogs(row)} style={{ fontFamily: 'monospace' }}>/{route}</a>
            ),
            sorter: (a, b) => a.route.localeCompare(b.route)
        },
        {
            title: t('Method'),
            dataIndex: 'method',
            key: 'method',
            width: 90,
            render: (method) => <Tag>{method}</Tag>
        },
        {
            title: t('HTTP Requests'),
            dataIndex: 'count',
            key: 'count',
            width: 140,
            defaultSortOrder: 'descend',
            sorter: (a, b) => a.count - b.count,
            render: (count) => (
                <Space direction="vertical" size={0}>
                    <strong>{count.toLocaleString()}</strong>
                    <span style={{ color: 'var(--text-2)', fontSize: 12 }}>
                        {totalRequests > 0 ? `%${((count / totalRequests) * 100).toFixed(1)}` : ''}
                    </span>
                </Space>
            )
        },
        {
            title: t('Errors'),
            key: 'errors',
            width: 150,
            sorter: (a, b) => (a.clientErrorCount + a.serverErrorCount) - (b.clientErrorCount + b.serverErrorCount),
            render: (_, row) => (
                <Space size={4}>
                    {row.clientErrorCount > 0 && (
                        <Tooltip title="4xx">
                            <Tag color="orange">{row.clientErrorCount.toLocaleString()}</Tag>
                        </Tooltip>
                    )}
                    {row.serverErrorCount > 0 && (
                        <Tooltip title="5xx">
                            <Tag color="red">{row.serverErrorCount.toLocaleString()}</Tag>
                        </Tooltip>
                    )}
                    {row.clientErrorCount === 0 && row.serverErrorCount === 0 && (
                        <span style={{ color: 'var(--text-2)' }}>-</span>
                    )}
                </Space>
            )
        },
        {
            title: t('Duration (ms)'),
            key: 'duration',
            width: 150,
            sorter: (a, b) => a.avgElapsedMs - b.avgElapsedMs,
            render: (_, row) => (
                <Space direction="vertical" size={0}>
                    <span>{t('avg')}: {row.avgElapsedMs.toLocaleString()}</span>
                    <span style={{ color: 'var(--text-2)', fontSize: 12 }}>
                        {t('max')}: {row.maxElapsedMs.toLocaleString()}
                    </span>
                </Space>
            )
        }
    ];

    return (
        <>
            <Space wrap style={{ marginBottom: 8 }}>
                <Select
                    style={{ width: 140 }}
                    value={minutes}
                    onChange={setMinutes}
                    options={RANGE_OPTIONS}
                />
                <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
                    {t('Refresh')}
                </Button>
                <span style={{ color: 'var(--text-2)' }}>
                    {rows.length} endpoint · {totalRequests.toLocaleString()} {t('HTTP Requests').toLowerCase()}
                </span>
            </Space>

            <Spin spinning={loading}>
                <Table
                    rowKey={(row) => `${row.method}:${row.route}`}
                    size="small"
                    columns={columns}
                    dataSource={rows}
                    // Dar ekranda kolonlar ezilmesin: tablo kendi içinde yatay kayar.
                    scroll={{ x: 'max-content' }}
                    pagination={{ pageSize: 50, size: 'small', showSizeChanger: true, pageSizeOptions: [20, 50, 100, 250] }}
                    locale={{ emptyText: <Empty description={t('No logs found')} /> }}
                />
            </Spin>
        </>
    );
}
